import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { setLanguage } from '../store/actions/UI';
import useLanguagePreference from './useLanguagePreference';
import useTypedDispatch from './useTypedDispatch';

export default function useLanguageNegotiator() {
  const history = useHistory();
  const dispatch = useTypedDispatch();

  const {
    preferredLanguage,
    currentLanguage,
    isCustomRoute,
  } = useLanguagePreference();

  const entityId = useSelector<ImmutableRootState<string | null>, string | null>(
    (state) => state.getIn(['entity', 'entityId'], null),
  );

  const preferredPath = useSelector<ImmutableRootState<string | null>, string | null>(
    (state) => state.getIn(['node', entityId, 'paths', preferredLanguage || currentLanguage], null),
  );

  const nodeLanguage = useSelector<ImmutableRootState<string>, string>(
    (state) => state.getIn(['node', entityId, 'langcode', 0, 'value'], currentLanguage),
  );

  const isFront = useSelector<ImmutableRootState<boolean>, boolean>(
    (state) => state.getIn(['node', entityId, 'is_front'], false),
  );

  const isLanguageDiscrepant = useMemo(() => (
    preferredLanguage !== nodeLanguage
  ), [nodeLanguage, preferredLanguage]);

  const search = useSelector<ImmutableRootState<string>, string>(
    (state) => state.getIn(['router', 'location', 'search'], ''),
  );

  useEffect(() => {
    // If language on the frontpage is different, navigate to correct frontpage.
    if (isFront && isLanguageDiscrepant && preferredLanguage) {
      history.replace(`/${preferredLanguage}`);
      return;
    }

    // If a Drupal node has the incorrect language, navigate to the correct path.
    if (!isCustomRoute && isLanguageDiscrepant && !!preferredPath && preferredLanguage) {
      history.replace(`${preferredPath}${search}`);
      return;
    }

    // If a custom route has the incorrect language,
    // update the language in the state to the correct one.
    if (isCustomRoute && preferredLanguage && preferredLanguage !== currentLanguage) {
      dispatch(setLanguage(preferredLanguage));
    }
  }, [
    isCustomRoute,
    isLanguageDiscrepant,
    preferredPath,
    history,
    currentLanguage,
    isFront,
    preferredLanguage,
    search,
    dispatch,
  ]);
}
